import '.ag{position:relative;}.ssr-plugin'
import '.a8{background-color:var(--palette-white);}.ssr-plugin'
import '.hr{border-radius:0 20px 20px 20px;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.e{flex-direction:column;}.ssr-plugin'
import '.a9{border:var(--border-subtle);}.ssr-plugin'
import '.at{overflow:hidden;}.ssr-plugin'
import '.hs{border-radius:0 19px 0 0;}.ssr-plugin'
import '.ht{height:23rem;}.ssr-plugin'
import '.c0{position:absolute;}.ssr-plugin'
import '.hu{top:-1px;}.ssr-plugin'
import '.hv{left:-1px;}.ssr-plugin'
import '.hw{transform:translateX(0%);}.ssr-plugin'
import '.hx{padding:0.1rem 0.375rem 0.375rem 0.1rem;}.ssr-plugin'
import '.hy{border-radius:0 0 1.125rem 0;}.ssr-plugin'
import '.e5{flex-shrink:1;}.ssr-plugin'
import '.hz{width:auto;}.ssr-plugin'
import '.i0{z-index:1;}.ssr-plugin'
import '.i1{top:0px;}.ssr-plugin'
import '.i2{right:-18px;}.ssr-plugin'
import '.i3{bottom:-20px;}.ssr-plugin'
import '.i4{left:0px;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import { Tag, Variant } from '@app/components/core/tag/Tag'
import { match } from 'ts-pattern'
import { InitiativeStatusRow } from './blocks/InitiativeStatusRow'
import Column from '@app/components/layout/Column'
import { useLocation } from 'wouter'
import { Image } from '@app/components/core/image/Image'

export interface InitiativeCardProps {
  image: string
  location: string
  phase?: string
  title: string
  description?: string
  tagText: string
  tagVariant?: Variant
  variant?: 'default' | 'spotlight'
  style?: Style
  to?: string
  onClick?: () => void
}

export const InitiativeCard = (props: InitiativeCardProps) => {
  const [_location, navigate] = useLocation()
  const defaults: Partial<InitiativeCardProps> = {
    tagVariant: 'accent',
    variant: 'default',
  }

  const finalProps = { ...defaults, ...props }

  // Default onClick handler that navigates to initiative page if slug is provided
  const handleClick = () => {
    if (finalProps.onClick) {
      finalProps.onClick()
    } else if (finalProps.to) {
      navigate(finalProps.to)
    }
  }

  return match(finalProps.variant)
    .with('default', () => (
      <DefaultCard {...finalProps} onClick={handleClick} />
    ))
    .with('spotlight', () => (
      <SpotlightCard {...finalProps} onClick={handleClick} />
    ))
    .exhaustive()
}

const DefaultCard = ({
  image,
  location,
  phase,
  title,
  description,
  tagText,
  tagVariant,
  style,
  onClick,
}: InitiativeCardProps) => {
  return (
    <styled.div className="ag a8 hr n d e a9"
      data-testid="initiative-card"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      <TagCutout tagText={tagText} tagVariant={tagVariant} />
      <div className="at hs"
      >
        <Image
          src={image}
          resizeWidth={500}
          style={{
            width: '100%',
            height: '224px',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </div>

      {/* Content Section */}
      <Column
        alignItems="stretch"
        style={{
          padding: '1.5rem',
          borderTop: 'var(--border-subtle)',
        }}
      >
        <InitiativeStatusRow location={location} status={phase} />
        {/* Title */}
        <Title.H4
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '120%',
            overflow: 'hidden',
            margin: 0,
          }}
        >
          {title}
        </Title.H4>
        {description && (
          <Text.Medium
            style={{
              color: 'var(--palette-blue-300)',
              lineHeight: '1.75rem',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Text.Medium>
        )}
      </Column>
    </styled.div>
  )
}

const SpotlightCard = ({
  image,
  location,
  phase,
  title,
  description,
  tagText,
  tagVariant,
  onClick,
  style,
}: InitiativeCardProps) => {
  return (
    <styled.div className="ag a8 hr at n d ht"
      data-testid="initiative-card"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      <TagCutout tagText={tagText} tagVariant={tagVariant} />
      {/* Image Section - Left Side */}
      <Image
        src={image}
        resizeHeight={361}
        style={{
          width: '361px',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
      />

      {/* Content Section - Right Side */}
      <Column
        alignItems="stretch"
        style={{
          padding: '1.5rem',
          flex: 1,
          justifyContent: 'flex-start',
          borderRight: 'var(--border-subtle)',
          borderBottom: 'var(--border-subtle)',
          borderTop: 'var(--border-subtle)',
          borderRadius: '0 20px 20px 0',
        }}
      >
        <InitiativeStatusRow location={location} status={phase} />
        {/* Title */}
        <Title.H4 style={{ marginTop: '2.5rem' }}>{title}</Title.H4>
        {/* Description - aligned to bottom */}
        <Text.Medium
          style={{
            marginTop: '1rem',
            color: 'var(--palette-blue-300)',
            lineHeight: '1.5',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {description}
        </Text.Medium>
      </Column>
    </styled.div>
  )
}

const TagCutout = ({
  tagText,
  tagVariant,
}: Pick<InitiativeCardProps, 'tagText' | 'tagVariant'>) => {
  return (
    <div className="c0 hu hv hw a8 hx hy e5 hz i0"
    >
      <div className="c0 i1 i2"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 0.00517004C11.8861 0.00178386 11.7718 0 11.6571 0C5.21908 0 0 5.37258 0 12L0 0L12 0V0.00517004Z"
            fill="var(--palette-white)"
          />
        </svg>
      </div>
      <Tag size="small" variant={tagVariant} label={tagText} />
      <div className="c0 i3 i4 hw"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 0.00517004C11.8861 0.00178386 11.7718 0 11.6571 0C5.21908 0 0 5.37258 0 12L0 0L12 0V0.00517004Z"
            fill="var(--palette-white)"
          />
        </svg>
      </div>
    </div>
  )
}
